import { useTranslation } from "@/hooks/useTranslation"
import { TitleStepDeployTab } from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import { DeploymentKeyButton } from "@/pages/DeploymentsPage/components/molecules/DeploymentKeyButton"
import { DownloadStep } from "@/pages/DeploymentsPage/components/organisms/DownloadStep"
import { type ScriptDeployCommandProps } from "@/pages/DeploymentsPage/components/organisms/ScriptDeployCommand"
import { AGENT_DEPLOYMENTS } from "@/pages/DeploymentsPage/const"
import { getPythonFiles } from "@/pages/DeploymentsPage/helpers/file"
import { useContainerImageSteps } from "@/pages/DeploymentsPage/hooks/useContainerImageSteps"
import { AgentPlatformStepCommand } from "./AgentPlatformStepCommand"

import type { StepItem } from "@/atoms"
import type { CommonDeployTabProps } from "@/pages/DeploymentsPage/components/molecules/DeployTab"
import type {
  AgentDeployment,
  AgentPlatform,
  AgentPlatformMethod,
  AgentPlatformStepPrefix
} from "./AgentPlatformTab.types"

// The deployment key input of every script gets the generate-key button
const DEPLOYMENT_KEY_BUTTONS: ScriptDeployCommandProps<string>["lineButtons"] =
  { deploymentKey: DeploymentKeyButton }

const STEP_PREFIX_KEYS: Record<AgentPlatformStepPrefix, string> = {
  add: "deployments.add",
  package: "deployments.package",
  store: "deployments.store",
  grant: "deployments.grant",
  deploy: "general.deploy"
}

// The methods that run the Kodem sensors in the agent: a container gets them in
// its image, code gets the Kodem Python package, then both run the platform's
// steps
export const useSensorMethods = (
  platform: AgentPlatform,
  props: CommonDeployTabProps
): AgentPlatformMethod[] => {
  const { noError, version } = props
  const { imageName, dockerPlatform, packageSpecArch } = platform
  const { t } = useTranslation("translation", { keyPrefix: "deployments" })
  const { t: tRoot } = useTranslation()
  const getContainerImageSteps = useContainerImageSteps(props)

  const getArtifactSteps: Record<AgentDeployment, () => StepItem[]> = {
    container: () =>
      getContainerImageSteps(imageName, dockerPlatform, {
        download: t("agentPlatform.downloadCredentialsStep"),
        dockerfile: t("agentPlatform.dockerfileStep"),
        build: t("agentPlatform.buildStep", { platform: dockerPlatform })
      }),
    codeUpload: () => [
      {
        title: (
          <DownloadStep
            noError={noError}
            files={getPythonFiles({ version, packageSpecArch })}
            titlePrefix={tRoot("general.download")}
            title={t("agentPlatform.downloadPackageStep", {
              arch: packageSpecArch
            })}
          />
        ),
        status: "process"
      }
    ]
  }

  return AGENT_DEPLOYMENTS.map((deployment) => ({
    key: deployment,
    label: t(`agentPlatform.deployment.${deployment}`),
    render: () => [
      ...getArtifactSteps[deployment](),
      ...platform.steps[deployment].map(
        ({ prefix, title, subTitle, ...step }) => ({
          title: (
            <TitleStepDeployTab
              prefix={tRoot(STEP_PREFIX_KEYS[prefix])}
              subTitle={subTitle && t(subTitle)}
            >
              {`- ${t(title)}`}
            </TitleStepDeployTab>
          ),
          description: (
            <AgentPlatformStepCommand
              // The steps of each method keep their own option state
              key={`${deployment}.${title}`}
              step={step}
              lineButtons={DEPLOYMENT_KEY_BUTTONS}
              getDefaultValues={platform.getDefaultValues}
              {...props}
            />
          ),
          status: "process" as const
        })
      )
    ]
  }))
}
